<script setup lang="ts">
/**
 * Table cell for a last-visit date. Same two-line layout as `StartDateCell`
 * (relative duration + absolute date), but colorizes the top line by freshness:
 *   recent (`≤ visitFreshness.recentDays`)  → `visit-cell--recent` (green)
 *   stale  (`≤ visitFreshness.staleDays`)   → `visit-cell--stale`  (amber)
 *   lost   (older)                          → `visit-cell--lost`   (red)
 *
 * Used in the Clients tab to give staff an at-a-glance signal about which
 * clients haven't visited recently.
 */
import { computed } from 'vue'
import { formatStartDate } from '@/utils/formatStartDate'
import { visitFreshness } from '@/config/visits'
import { MS_PER_DAY } from '@/utils/time'

const props = withDefaults(
  defineProps<{
    date?: Date | string
    emptyPlaceholder?: string
    locale?: string
    dateStyle?: 'long' | 'short' | 'medium'
    timeZone?: string
    maxWidth?: string
  }>(),
  { locale: undefined, dateStyle: undefined, timeZone: undefined },
)

const hasDate = computed(
  () =>
    props.date !== undefined &&
    props.date !== null &&
    (typeof props.date !== 'string' || props.date.trim() !== ''),
)

const result = computed(() =>
  hasDate.value
    ? formatStartDate({
        date: props.date!,
        locale: props.locale,
        dateStyle: props.dateStyle,
        timeZone: props.timeZone,
      })
    : null,
)

const freshnessClass = computed(() => {
  if (!hasDate.value || !props.date) return ''
  const parsed = typeof props.date === 'string' ? new Date(props.date) : props.date
  if (Number.isNaN(parsed.getTime())) return ''

  const daysAgo = (Date.now() - parsed.getTime()) / MS_PER_DAY
  if (daysAgo <= visitFreshness.recentDays) return 'visit-cell--recent'
  if (daysAgo <= visitFreshness.staleDays) return 'visit-cell--stale'
  return 'visit-cell--lost'
})
</script>

<template>
  <td
    class="font-tufte text-tufte-text"
    :style="props.maxWidth ? { maxWidth: props.maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <div v-if="result" class="flex flex-col gap-0.5">
      <!--
        Freshness coloring (recent / stale / lost) is applied only to the
        relative-duration line. The absolute date below is rendered muted
        (matching `StartDateCell` and other two-line date cells) so it
        consistently reads as secondary metadata regardless of freshness.
      -->
      <span :class="freshnessClass">{{ result.durationSince }}</span>
      <span class="text-tufte-muted" style="font-size: var(--text-tufte-xs)">
        {{ result.formattedDate }}
      </span>
    </div>
    <span v-else-if="props.emptyPlaceholder" class="text-tufte-muted">{{
      props.emptyPlaceholder
    }}</span>
  </td>
</template>
